import {ExpandedGroupIds} from '@hconnect/common/hooks/useExpandGroups'
import {
  EquipmentPerformance,
  KpiCalculationTimeFrameDto,
  KpiResult,
  CalculationTimeFrameType,
  KpiAssignment
} from '@hconnect/common/types'
import {CollapsibleTableWithoutPagination, ColumnsConfig} from '@hconnect/uikit/src/lib2'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {Box, TableCell, TableRow, Typography, styled} from '@mui/material'
import {TFunction} from 'i18next'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {KPIS_WIDGET_NAMESPACE} from '../consts'

import {KpiValue} from './KpiValue'

const ORDERED_TIME_FRAMES_TO_DISPLAY: CalculationTimeFrameType[] = [
  CalculationTimeFrameType.CustomDateRange,
  CalculationTimeFrameType.CurrentShift,
  CalculationTimeFrameType.PastShift,
  CalculationTimeFrameType.ShiftBeforePastShift,
  CalculationTimeFrameType.Today,
  CalculationTimeFrameType.Last24Hours,
  CalculationTimeFrameType.Yesterday,
  CalculationTimeFrameType.TheDayBeforeYesterday,
  CalculationTimeFrameType.LastWeekend,
  CalculationTimeFrameType.TheWeekendBeforeLast,
  CalculationTimeFrameType.Last3Days,
  CalculationTimeFrameType.Last7Days,
  CalculationTimeFrameType.Last30Days,
  CalculationTimeFrameType.LastMonth,
  CalculationTimeFrameType.MonthBeforeLastMonth
]

const getOrderedTimeFrameKeys = (
  orderedTimeFramesToDisplay: CalculationTimeFrameType[],
  timeFrames: KpiCalculationTimeFrameDto[]
): CalculationTimeFrameType[] => {
  return orderedTimeFramesToDisplay.filter((orderedTimeFrame) =>
    timeFrames.some((tf) => tf.calculationTimeFrameType === orderedTimeFrame)
  )
}

const getColumnsConfig = (timeFrames: CalculationTimeFrameType[], t: TFunction) => {
  const config: ColumnsConfig<EquipmentPerformance> = [
    {
      key: 'kpiName',
      label: t('equipmentKpi.label.kpi'),
      template: ({rowData}) => {
        return <Typography variant="h5">{rowData.equipment.text}</Typography>
      },
      headerSx: {
        width: '45%'
      }
    },
    {
      key: 'kpiUnit',
      label: t('equipmentKpi.label.unit'),
      headerSx: {
        textAlign: 'right',
        paddingLeft: 0,
        paddingRight: 0
      }
    }
  ]

  timeFrames.forEach((timeFrame) => {
    config.push({
      key: timeFrame,
      label: t(`equipmentKpi.timeFrameType.${timeFrame}`),
      headerSx: {
        textAlign: 'right',
        paddingLeft: 4
      }
    })
  })

  return config
}

const StyledTableCell = styled(TableCell)(({theme}) => ({
  fontSize: 16,
  paddingTop: theme.spacing(2.5),
  paddingBottom: theme.spacing(2.5)
}))

const StyledBoxNoData = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5)
}))

const StyledTableCellUnit = styled(StyledTableCell)(({theme}) => ({
  textAlign: 'right',
  paddingLeft: theme.spacing(0),
  paddingRight: theme.spacing(0)
}))

const getKpiUnit = (kpiAssignment: KpiAssignment): string => {
  const timeFrameResults = kpiAssignment.timeFrameResults

  if (timeFrameResults) {
    for (const key in timeFrameResults) {
      const unit = timeFrameResults[key]?.unit
      if (unit !== null) {
        return unit
      }
    }
  }

  return ''
}

type Props = {
  showKpiStatusIndicator?: boolean
  data: EquipmentPerformance[]
  expandAll?: boolean
  expandedEquipments?: string[]
  timeFrames: KpiCalculationTimeFrameDto[]
  toggleExpandedEquipments?: (id: ExpandedGroupIds) => void
  maxHeight?: number | string
}

export const EquipmentKpiTableOverview: React.FC<Props> = ({
  data,
  showKpiStatusIndicator = false,
  expandedEquipments,
  timeFrames,
  toggleExpandedEquipments,
  maxHeight
}) => {
  const {t, i18n} = useTranslation(KPIS_WIDGET_NAMESPACE)
  const language = i18n.language
  const orderedTimeFrameKeys = getOrderedTimeFrameKeys(ORDERED_TIME_FRAMES_TO_DISPLAY, timeFrames)
  const columnsConfig = getColumnsConfig(orderedTimeFrameKeys, t)
  return (
    <Box data-test-id="equipment-kpi-table-overview">
      <CollapsibleTableWithoutPagination<EquipmentPerformance>
        config={columnsConfig}
        data={data}
        maxHeight={maxHeight}
        emptyMessage={t('equipmentKpi.message.noData')}
        expandedRows={expandedEquipments?.map(Number)}
        setExpandedRows={(index: number) => toggleExpandedEquipments?.(String(index))}
        collapseContent={({rowData}) => (
          <>
            {rowData.kpiPerformance?.kpiAssignments.map((kpiAssignment, index) => {
              return (
                <TableRow
                  key={`table-collapse-row-${index}-${kpiAssignment.kpiName}`}
                  data-test-id={`table-collapse-row-${index}-${kpiAssignment.kpiName}`}
                >
                  <StyledTableCell />
                  <StyledTableCell>
                    <Typography variant="body1">{kpiAssignment.kpiName}</Typography>
                  </StyledTableCell>
                  <StyledTableCellUnit>
                    <Typography variant="body1">{getKpiUnit(kpiAssignment)}</Typography>
                  </StyledTableCellUnit>
                  {orderedTimeFrameKeys.map((timeFrame) => {
                    const kpiResult: KpiResult = kpiAssignment.timeFrameResults![timeFrame]
                    if (!kpiResult)
                      return (
                        <StyledTableCell key={`table-cell-${timeFrame}`}>
                          <StyledBoxNoData>
                            <InfoOutlinedIcon sx={{m: 1}} />
                            <Typography variant="body1" sx={{whiteSpace: 'nowrap'}}>
                              {t('equipmentKpi.message.noData')}
                            </Typography>
                          </StyledBoxNoData>
                        </StyledTableCell>
                      )

                    return (
                      <StyledTableCell key={`table-cell-${timeFrame}`}>
                        <KpiValue
                          value={kpiResult.value}
                          language={language}
                          showKpiStatusIndicator={showKpiStatusIndicator}
                          kpiBreachSeverity={kpiResult.kpiBreachSeverity}
                          dataTestId={`equipment-kpi-table-threshold-icon-${kpiResult.kpiBreachSeverity}`}
                        />
                      </StyledTableCell>
                    )
                  })}
                </TableRow>
              )
            })}
          </>
        )}
      />
    </Box>
  )
}
